import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Upload, ScrollText, Settings, LogOut, FileText, Menu, X,
} from "lucide-react";

const NavItem = ({ to, icon: Icon, label, testId, end, onNavigate }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={testId}
    onClick={onNavigate}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm transition-colors ${
        isActive
          ? "bg-white text-[#0F2044] font-semibold shadow-sm"
          : "text-slate-300 hover:bg-white/5 hover:text-white"
      }`
    }
  >
    <Icon size={18} />
    <span>{label}</span>
  </NavLink>
);

export const AppShell = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false); // mobile drawer state

  // Close drawer on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const canUpload = user?.role === "admin" || user?.role === "finance";
  const canAdmin = user?.role === "admin";

  const closeDrawer = () => setOpen(false);

  const NavLinks = () => (
    <>
      <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" testId="nav-dashboard" onNavigate={closeDrawer} />
      {canUpload && (
        <NavItem to="/upload" icon={Upload} label="New Shipment" testId="nav-upload" onNavigate={closeDrawer} />
      )}
      <NavItem to="/shipments" icon={FileText} label="Shipments" testId="nav-shipments" onNavigate={closeDrawer} />
      {canAdmin && (
        <NavItem to="/audit" icon={ScrollText} label="Audit Trail" testId="nav-audit" onNavigate={closeDrawer} />
      )}
      {canAdmin && (
        <NavItem to="/settings" icon={Settings} label="Settings" testId="nav-settings" onNavigate={closeDrawer} />
      )}
    </>
  );

  const SidebarBody = () => (
    <>
      <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
        <div>
          <div className="text-white font-bold text-lg tracking-tight leading-tight">
            Ray Intl <span className="text-slate-400">|</span> Radish Tech
          </div>
          <div className="text-[10px] text-slate-400 uppercase tracking-widest mt-1">
            Duty Reconciliation
          </div>
        </div>
        <button
          onClick={closeDrawer}
          className="lg:hidden text-slate-300 hover:text-white p-1 -mr-1"
          data-testid="drawer-close"
          aria-label="Close menu"
        >
          <X size={22} />
        </button>
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        <NavLinks />
      </nav>

      <div className="p-3 border-t border-white/10">
        <div className="px-4 py-2 text-xs text-slate-400">
          <div className="font-semibold text-slate-200">{user?.username}</div>
          <div className="uppercase tracking-wider">{user?.role}</div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
        >
          <LogOut size={18} />
          <span>Sign out</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex" style={{ background: "var(--color-bg)" }}>
      {/* Desktop sidebar (>= lg) */}
      <aside
        className="hidden lg:flex w-64 flex-col fixed inset-y-0 left-0"
        style={{ background: "#0F2044" }}
      >
        <SidebarBody />
      </aside>

      {/* Mobile drawer (< lg) */}
      {open && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
          onClick={closeDrawer}
          data-testid="drawer-backdrop"
        />
      )}
      <aside
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-64 flex flex-col transition-transform duration-200 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ background: "#0F2044" }}
        data-testid="mobile-drawer"
      >
        <SidebarBody />
      </aside>

      {/* Main column: leaves room for desktop sidebar (64) */}
      <div className="flex-1 flex flex-col min-w-0 lg:pl-64">
        {/* Mobile top bar (< lg) */}
        <header
          className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 text-white shadow-md"
          style={{ background: "#0F2044" }}
        >
          <button
            onClick={() => setOpen(true)}
            className="p-1 -ml-1 rounded-md hover:bg-white/10 transition-colors"
            data-testid="drawer-open"
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>
          <div className="text-sm font-bold tracking-tight">
            Ray Intl <span className="text-slate-400">|</span> Radish Tech
          </div>
          <div className="w-8" /> {/* spacer to balance the hamburger */}
        </header>

        <main className="flex-1 overflow-auto">
          <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AppShell;
